import React, { useCallback, useMemo, useState } from "react";
import { Button, Checkbox } from "@itwin/itwinui-react";
import { featureTree, type FeatureNode, type FeatureNodeId } from "./featureTreeData";

type NodeCheckState = "checked" | "unchecked" | "indeterminate";

export interface FeatureFilterPanelProps {
  onSelectionChange?: (selectedLeafIds: FeatureNodeId[]) => void;
  onActionClick?: (
    selectedLeafIds: FeatureNodeId[],
    options: {
      includeAsset: boolean;
      includeAsbuilt: boolean;
      include2d: boolean;
      include3d: boolean;
    }
  ) => void;
  actionButtonLabel?: string;
  optionLabels?: {
    primary: string;
    secondary: string;
  };
  hideDefaultOptions?: boolean;
  extraControls?: React.ReactNode;
  disableAction?: boolean;
  statusContent?: React.ReactNode;
}

/**
 * Collect all leaf node IDs under a given node.
 */
function getDescendantLeafIds(node: FeatureNode): FeatureNodeId[] {
  if (!node.children || node.children.length === 0) {
    return [node.id];
  }
  const ids: FeatureNodeId[] = [];
  for (const child of node.children) {
    ids.push(...getDescendantLeafIds(child));
  }
  return ids;
}

/**
 * Compute the check state for a node based on which leaf IDs are selected.
 */
function computeNodeCheckState(
  node: FeatureNode,
  selected: Set<FeatureNodeId>
): NodeCheckState {
  if (!node.children || node.children.length === 0) {
    return selected.has(node.id) ? "checked" : "unchecked";
  }

  let anyChecked = false;
  let anyUnchecked = false;

  for (const child of node.children) {
    const childState = computeNodeCheckState(child, selected);
    if (childState === "indeterminate") {
      return "indeterminate";
    }
    if (childState === "checked") {
      anyChecked = true;
    } else {
      anyUnchecked = true;
    }
  }

  if (anyChecked && anyUnchecked) return "indeterminate";
  if (anyChecked) return "checked";
  return "unchecked";
}

/**
 * Render a node (and its children) recursively.
 */
function renderFeatureNode(
  node: FeatureNode,
  depth: number,
  selected: Set<FeatureNodeId>,
  expanded: Set<FeatureNodeId>,
  toggleNode: (node: FeatureNode) => void,
  toggleExpand: (node: FeatureNode) => void
): JSX.Element {
  const state = computeNodeCheckState(node, selected);
  const isChecked = state === "checked";
  const isIndeterminate = state === "indeterminate";

  const hasChildren = !!node.children && node.children.length > 0;
  const isExpanded = expanded.has(node.id);

  return (
    <div key={node.id} style={{ marginLeft: depth * 16, marginBottom: 4 }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        {hasChildren ? (
          <button
            type="button"
            onClick={() => toggleExpand(node)}
            style={{
              marginRight: 4,
              width: 18,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            {isExpanded ? "−" : "+"}
          </button>
        ) : (
          <span style={{ display: "inline-block", width: 18, marginRight: 4 }} />
        )}

        <Checkbox
          checked={isChecked}
          indeterminate={isIndeterminate}
          onChange={() => toggleNode(node)}
        />
        <span style={{ marginLeft: 4 }}>{node.label}</span>
      </div>

      {hasChildren &&
        isExpanded &&
        node.children!.map((child) =>
          renderFeatureNode(
            child,
            depth + 1,
            selected,
            expanded,
            toggleNode,
            toggleExpand
          )
        )}
    </div>
  );
}

export const FeatureFilterPanel: React.FC<FeatureFilterPanelProps> = ({
  onSelectionChange,
  onActionClick,
  actionButtonLabel = "Export",
  optionLabels = {
    primary: "Asset",
    secondary: "Asbuilt",
  },
  hideDefaultOptions = false,
  extraControls,
  disableAction = false,
  statusContent,
}) => {
  const [selectedLeafIds, setSelectedLeafIds] = useState<FeatureNodeId[]>([]);
  const [includeAsset, setIncludeAsset] = useState(true);
  const [includeAsbuilt, setIncludeAsbuilt] = useState(true);
  // start with all top-level nodes expanded
  const [expandedIds, setExpandedIds] = useState<FeatureNodeId[]>(
    () => featureTree.map((n) => n.id)
  );

  const selectedSet = useMemo(
    () => new Set<FeatureNodeId>(selectedLeafIds),
    [selectedLeafIds]
  );

  const expandedSet = useMemo(
    () => new Set<FeatureNodeId>(expandedIds),
    [expandedIds]
  );

  const toggleNode = useCallback(
    (node: FeatureNode) => {
      const leafIds = getDescendantLeafIds(node);
      const next = new Set<FeatureNodeId>(selectedSet);

      const currentState = computeNodeCheckState(node, selectedSet);
      const shouldSelect = currentState === "unchecked";

      if (shouldSelect) {
        for (const id of leafIds) next.add(id);
      } else {
        for (const id of leafIds) next.delete(id);
      }

      const result = Array.from(next);
      setSelectedLeafIds(result);
      onSelectionChange?.(result);
    },
    [selectedSet, onSelectionChange]
  );

  const toggleExpand = useCallback(
    (node: FeatureNode) => {
      if (!node.children || node.children.length === 0) return;
      const next = new Set<FeatureNodeId>(expandedSet);
      if (next.has(node.id)) {
        next.delete(node.id);
      } else {
        next.add(node.id);
      }
      setExpandedIds(Array.from(next));
    },
    [expandedSet]
  );

  const handleActionClick = useCallback(() => {
    if (!hideDefaultOptions && !includeAsset && !includeAsbuilt) return;
    onActionClick?.(selectedLeafIds, {
      includeAsset,
      includeAsbuilt,
      include2d: includeAsset,
      include3d: includeAsbuilt,
    });
  }, [hideDefaultOptions, onActionClick, selectedLeafIds, includeAsset, includeAsbuilt]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 8,
      }}
    >
      <div style={{ marginBottom: 8, fontWeight: 600, fontSize: 14 }}>
        Feature Filters
      </div>

      {/* Scrollable body */}
      <div style={{ flex: "1 1 auto", overflow: "auto" }}>
        {featureTree.map((node) =>
          renderFeatureNode(
            node,
            0,
            selectedSet,
            expandedSet,
            toggleNode,
            toggleExpand
          )
        )}
      </div>

      {/* Footer with Export buttons */}
      {!hideDefaultOptions && (
        <div
          style={{
            flex: "0 0 auto",
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <Checkbox
            label={optionLabels.primary}
            checked={includeAsset}
            onChange={(e) => setIncludeAsset(Boolean(e.target.checked))}
          />
          <Checkbox
            label={optionLabels.secondary}
            checked={includeAsbuilt}
            onChange={(e) => setIncludeAsbuilt(Boolean(e.target.checked))}
          />
        </div>
      )}

      {extraControls && (
        <div
          style={{
            flex: "0 0 auto",
            marginTop: 8,
          }}
        >
          {extraControls}
        </div>
      )}

      <div
        style={{
          flex: "0 0 auto",
          marginTop: 8,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Button
          size="small"
          onClick={handleActionClick}
          disabled={disableAction || (!hideDefaultOptions && !includeAsset && !includeAsbuilt)}
        >
          {actionButtonLabel}
        </Button>
      </div>
      {statusContent && (
        <div
          style={{
            flex: "0 0 auto",
            marginTop: 8,
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          {statusContent}
        </div>
      )}
    </div>
  );
};



